import React, { useState, useEffect, useCallback } from 'react';
import api from '../../api/client';
import { useToast, Button, Spinner, Modal, Input, Textarea, Badge, Icon } from '../../components/ds';
import './PassbackPage.css';

export default function PassbackPage() {
  const [passbacks, setPassbacks] = useState([]);
  const [placementTypes, setPlacementTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPb, setEditingPb] = useState(null); // null = closed, {} = new, {id,...} = edit
  const { addToast } = useToast();

  const companyID = sessionStorage.getItem('portal_company_id');

  const fetchData = useCallback(async () => {
    try {
      const [pbData, ptData] = await Promise.all([
        api.getPassbacks(companyID),
        api.getPortalPlacementTypes(),
      ]);
      setPassbacks(pbData?.passbacks || []);
      setPlacementTypes(ptData || []);
    } catch (err) {
      addToast('載入失敗：' + err.message);
    } finally {
      setLoading(false);
    }
  }, [companyID, addToast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDelete = async (pb) => {
    if (!window.confirm(`確定要刪除「${pb.name}」？`)) return;
    try {
      await api.deletePassback(pb.id);
      addToast('已刪除', 'success');
      fetchData();
    } catch (err) {
      addToast('刪除失敗：' + err.message);
    }
  };

  const handleSaved = () => {
    setEditingPb(null);
    fetchData();
  };

  // Group passbacks by placement_type_id
  const grouped = {};
  for (const pt of placementTypes) {
    grouped[pt.name] = {
      displayName: pt.display_name || pt.name,
      passbacks: [],
    };
  }
  for (const pb of passbacks) {
    if (!grouped[pb.placement_type_id]) {
      grouped[pb.placement_type_id] = {
        displayName: pb.placement_type_name || pb.placement_type_id,
        passbacks: [],
      };
    }
    grouped[pb.placement_type_id].passbacks.push(pb);
  }

  if (loading) {
    return <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><Spinner /></div>;
  }

  return (
    <div>
      <h1 className="h1 h1--lg" style={{ marginBottom: 24 }}>Passback 設定</h1>

      {Object.entries(grouped).map(([ptId, group]) => (
        <div key={ptId} className="pb-group">
          <div className="pb-group-header">
            <h2 className="pb-group-title">
              {group.displayName}
              <span className="pb-group-count">{group.passbacks.length} 組</span>
            </h2>
            <Button onClick={() => setEditingPb({ placement_type_id: ptId, placement_type_name: group.displayName })}>
              <Icon name="plus" size={14} />
              新增 Passback
            </Button>
          </div>

          {group.passbacks.length === 0 ? (
            <div className="pb-empty">
              <Icon name="list" size={24} color="var(--gray-300)" style={{ marginBottom: 8 }} />
              <div>尚未設定 Passback</div>
            </div>
          ) : (
            <div className="pb-list">
              {group.passbacks.map((pb) => (
                <div key={pb.id} className="pb-item">
                  <div className="pb-item-header">
                    <div className="pb-item-name">
                      {pb.name}
                      {pb.is_default && <Badge variant="primary">預設</Badge>}
                    </div>
                    <div className="pb-item-actions">
                      <button className="btn btn-sm btn-ghost" onClick={() => setEditingPb(pb)}>
                        <Icon name="edit-2" size={14} />
                        編輯
                      </button>
                      <button className="btn btn-sm btn-ghost" style={{ color: '#EF4444' }} onClick={() => handleDelete(pb)}>
                        <Icon name="trash-2" size={14} />
                        刪除
                      </button>
                    </div>
                  </div>
                  <pre className="pb-item-code">{pb.code}</pre>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}

      {placementTypes.length === 0 && passbacks.length === 0 && (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-tertiary)' }}>
          沒有可用的版位類型，請聯繫管理員設定。
        </div>
      )}

      {editingPb && (
        <PassbackModal
          passback={editingPb}
          companyID={companyID}
          onClose={() => setEditingPb(null)}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}

function PassbackModal({ passback, companyID, onClose, onSaved }) {
  const isEdit = !!passback.id;
  const [name, setName] = useState(passback.name || '');
  const [code, setCode] = useState(passback.code || '');
  const [isDefault, setIsDefault] = useState(passback.is_default || false);
  const [saving, setSaving] = useState(false);
  const { addToast } = useToast();

  const handleSubmit = async () => {
    if (!name.trim() || !code.trim()) {
      addToast('請填寫名稱和代碼', 'error');
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        await api.updatePassback(passback.id, { name, code, is_default: isDefault });
        addToast('已更新', 'success');
      } else {
        await api.createPassback({
          company_uuid: companyID,
          placement_type_id: passback.placement_type_id,
          name,
          code,
          is_default: isDefault,
        });
        addToast('已新增', 'success');
      }
      onSaved();
    } catch (err) {
      addToast((isEdit ? '更新' : '新增') + '失敗：' + err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={isEdit ? '編輯 Passback' : '新增 Passback'}
      onClose={onClose}
      footer={
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variant="secondary" onClick={onClose}>取消</Button>
          <Button onClick={handleSubmit} disabled={saving}>
            {saving ? '儲存中...' : '儲存'}
          </Button>
        </div>
      }
    >
      <div style={{ marginBottom: 12 }}>
        <label className="meta" style={{ fontWeight: 500, display: 'block', marginBottom: 4 }}>版位類型</label>
        <div className="body" style={{ color: 'var(--text-secondary)' }}>
          {passback.placement_type_name || passback.placement_type_id}
        </div>
      </div>

      <div style={{ marginBottom: 12 }}>
        <Input
          label="名稱"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="例如：Google AdSense"
        />
      </div>

      <div style={{ marginBottom: 12 }}>
        <Textarea
          label="Passback 代碼"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder="<script>...</script>"
          rows={7}
          style={{ fontFamily: "'SF Mono', 'Fira Code', 'Fira Mono', 'Consolas', monospace", fontSize: 13 }}
        />
      </div>

      <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
        <input type="checkbox" checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} />
        <span className="body">設為預設</span>
      </label>
    </Modal>
  );
}
